"use client";

import { linkHoverColor } from "@faire/retailer-visitor-shared/components/Header/Colors";
import { topNavEvent } from "@faire/retailer-visitor-shared/components/Header/Events";
import { IconWithCountBadge } from "@faire/retailer-visitor-shared/components/Header/icons/IconWithCountBadge";
import { HeaderIconContainer } from "@faire/retailer-visitor-shared/components/Header/styled";
import { HeaderLink } from "@faire/retailer-visitor-shared/components/Header/styled";
import { useCartItems } from "@faire/retailer-visitor-shared/hooks/cart/useCartItems";
import { useCartPrefetch } from "@faire/retailer-visitor-shared/hooks/cart/useCartPrefetch";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import {
  TimingRecorder,
  TIMING_KEYS,
} from "@faire/retailer-visitor-shared/lib/actionLatencyTools";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { CartEmpty } from "@faire/web--source/slate/icons/foundation/CartEmpty";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackRetailersGlobalNavCartClick } from "@faire/web-api--source/events/retailers/click/globalNavCart";
import cart from "@faire/web-api--source/routes/www/cart";
import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

const Container = styled.div`
  display: flex;
  ${HeaderLink} {
    padding: 0;
  }

  ${responsiveMediaQueries.canHover} {
    &:hover {
      svg {
        color: ${linkHoverColor};
      }
    }
  }
`;

const HeaderCartBase = (props: HeaderCart.IProps) => {
  const { className, dataTestId } = props;
  const { isDesktopAndAbove } = useViewport();
  const { totalBrandsInCartFormatted } = useCartItems();
  const prefetchCart = useCartPrefetch();
  const { retailerToken } = useRetailer();
  const cartPageLink = cart();
  const onCartLinkMouseEnter = () => prefetchCart(cartPageLink);

  const handleCartClick = () => {
    TimingRecorder.get().recordTime(TIMING_KEYS.VIEW_CART);
    topNavEvent("cart", retailerToken ?? "");
    trackRetailersGlobalNavCartClick();

    if (!isDesktopAndAbove) {
      getWindowOrThrow().scrollTo?.({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  return (
    <Container className={className}>
      {isDesktopAndAbove ? (
        <>
          <HeaderLink
            data-test-id={dataTestId}
            to={cartPageLink}
            onClick={handleCartClick}
            onMouseEnter={onCartLinkMouseEnter}
            style={{ height: "unset" }}
          >
            <HeaderCartIcon
              totalBrandsInCartFormatted={totalBrandsInCartFormatted}
            />
          </HeaderLink>
        </>
      ) : (
        <HeaderLink
          data-test-id={dataTestId}
          onClick={handleCartClick}
          to={cartPageLink}
        >
          <HeaderCartIcon
            totalBrandsInCartFormatted={totalBrandsInCartFormatted}
          />
        </HeaderLink>
      )}
    </Container>
  );
};

export const HeaderCart = observer(HeaderCartBase);

const HeaderCartIcon: React.FC<{
  totalBrandsInCartFormatted: string | undefined;
}> = ({ totalBrandsInCartFormatted }) => {
  const { strictLocalize } = useStrictLocalization();
  const brandCount =
    (totalBrandsInCartFormatted?.length ?? 0) <= 3
      ? totalBrandsInCartFormatted
      : strictLocalize({
          defaultMessage: "999+",
          description: {
            text: "Count shown on bag next to cart when there are more than 999 brands in cart. String cannot exceed 4 characters.",
            img: "https://cdn.faire.com/fastly/98b3ab0e39d495bc565e5e2b90a4d1b52c6c4fb327fd57d1a4c6e63c08747736.png",
          },
        });

  return (
    <ExpandingHeaderIconContainer>
      <IconWithCountBadge
        // TODO: use meaningful localized string instead, this is shown to screenreaders
        icon={<CartEmpty fontSize="20px" titleAccess="CartEmpty" />}
        count={brandCount}
      />
    </ExpandingHeaderIconContainer>
  );
};

const ExpandingHeaderIconContainer = styled(HeaderIconContainer)`
  width: 100%;
`;

namespace HeaderCart {
  export interface IProps {
    className?: string;
    dataTestId: string;
  }
}
